import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Chart as ChartJS, registerables } from 'chart.js';
import { SampleDataBadge } from '../../common';

// Register Chart.js components
ChartJS.register(...registerables);

// Expand icon
const ExpandIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path
            d="M4 16V12M4 16H8M4 16L9 11M16 4V8M16 4H12M16 4L11 9"
            stroke="#666666"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

/**
 * Trainings Completed vs Quizzes Solved Chart
 * Area chart showing trainings and quizzes over months
 */
const MBFTrainingsQuizzesChart = ({ data = [], isSampleData = false, onMaximize, isFullscreen = false }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Default data matching the Figma design exactly
    const defaultData = [
        { month: 'Jan', trainings_completed: 45, quizzes_solved: 20 },
        { month: 'Feb', trainings_completed: 20, quizzes_solved: 15 },
        { month: 'Mar', trainings_completed: 55, quizzes_solved: 55 },  // quizzes_solved meets trainings_completed
        { month: 'Apr', trainings_completed: 90, quizzes_solved: 25 },
        { month: 'May', trainings_completed: 40, quizzes_solved: 20 },
        { month: 'Jun', trainings_completed: 35, quizzes_solved: 15 },
        { month: 'Jul', trainings_completed: 55, quizzes_solved: 18 },
        { month: 'Aug', trainings_completed: 100, quizzes_solved: 22 },
        { month: 'Sep', trainings_completed: 70, quizzes_solved: 70 },  // quizzes_solved meets trainings_completed
        { month: 'Oct', trainings_completed: 65, quizzes_solved: 55 },  // quizzes_solved close to trainings_completed
        { month: 'Nov', trainings_completed: 30, quizzes_solved: 25 },
        { month: 'Dec', trainings_completed: 90, quizzes_solved: 25 },
    ];

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.month || item.label);
    const trainings = chartData.map(item => item.trainings_completed || 0);
    const quizzes = chartData.map(item => item.quizzes_solved || 0);

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const ctx = chartRef.current.getContext('2d');
            const fontSize = isMobile ? 10 : 12;

            // Create gradient for trainings area (blue)
            const trainingsGradient = ctx.createLinearGradient(0, 0, 0, 300);
            trainingsGradient.addColorStop(0, 'rgba(180, 180, 230, 0.7)');
            trainingsGradient.addColorStop(1, 'rgba(200, 200, 240, 0.3)');

            // Create gradient for quizzes area (green)
            const quizzesGradient = ctx.createLinearGradient(0, 0, 0, 300);
            quizzesGradient.addColorStop(0, 'rgba(200, 230, 150, 0.9)');
            quizzesGradient.addColorStop(1, 'rgba(200, 230, 150, 0.5)');

            chartInstance.current = new ChartJS(ctx, {
                type: 'line',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Trainings Completed',
                            data: trainings,
                            borderColor: '#4444aa',
                            backgroundColor: trainingsGradient,
                            borderWidth: 2,
                            tension: 0, // Angular/zigzag lines
                            pointRadius: 3,
                            pointBackgroundColor: '#4444aa',
                            fill: true,
                            order: 1, // Draw behind quizzes
                        },
                        {
                            label: 'Quizzes Solved',
                            data: quizzes,
                            borderColor: '#88bb44',
                            backgroundColor: quizzesGradient,
                            borderWidth: 2,
                            tension: 0, // Angular/zigzag lines
                            pointRadius: 0,
                            fill: true,
                            order: 2, // Draw in front
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: true,
                            position: 'bottom',
                            labels: {
                                boxWidth: 12,
                                boxHeight: 12,
                                padding: 15,
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: 11,
                                },
                                color: '#333333',
                            },
                        },
                        tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            titleColor: '#333333',
                            bodyColor: '#333333',
                            borderColor: '#e6e6e6',
                            borderWidth: 1,
                            padding: 10,
                            cornerRadius: 8,
                        },
                        datalabels: { display: false },
                    },
                    scales: {
                        x: {
                            grid: { display: false },
                            border: { display: false },
                            ticks: {
                                color: '#333333',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: fontSize,
                                },
                            },
                            title: {
                                display: true,
                                text: 'Months',
                                color: '#333333',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: 12,
                                    weight: '500',
                                },
                            },
                        },
                        y: {
                            min: 0,
                            max: 100,
                            ticks: {
                                stepSize: 20,
                                color: '#666666',
                                font: {
                                    family: 'Arial, sans-serif',
                                    size: fontSize,
                                },
                            },
                            border: { display: false },
                            grid: { color: '#f0f0f0' },
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, chartData, labels, trainings, quizzes]);

    // Fullscreen mode - render just the chart without wrapper
    if (isFullscreen) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <canvas ref={chartRef} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: '#ffffff',
                borderRadius: '12px',
                border: '1px solid #e6e6e6',
                display: 'flex',
                flexDirection: 'column',
                height: '408px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    bgcolor: '#ffffff',
                    borderBottom: '1px solid #e6e6e6',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography
                        sx={{
                            color: '#333333',
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: '16px',
                            lineHeight: '20px',
                            fontWeight: 500,
                        }}
                    >
                        Trainings Completed V/S Quizzes Solved
                    </Typography>
                    {isSampleData && <SampleDataBadge absolute={false} />}
                </Box>

                <IconButton
                    size="small"
                    onClick={onMaximize}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #e6e6e6',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: '#f9f9f9' },
                    }}
                >
                    <ExpandIcon />
                </IconButton>
            </Box>

            {/* Chart Area */}
            <Box
                sx={{
                    flex: 1,
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    bgcolor: '#fafafa',
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        bgcolor: '#ffffff',
                        borderRadius: '8px',
                        border: '1px solid #e6e6e6',
                        padding: '16px',
                        minHeight: 0,
                    }}
                >
                    <canvas ref={chartRef} />
                </Box>
            </Box>
        </Box>
    );
};

export default MBFTrainingsQuizzesChart;
